/* eslint no-param-reassign:  ["error", { "props": false }] */
/* eslint no-shadow: 0 */

import { get, debounce } from 'lodash';

import { getOrganizerProjectList,
  getProjectEditDetails,
  editProject,
  removeMedia,
  reorderProjectMedia,
  addProjectMediaUrl,
  getProjectEditDefaults,
  getProjectDetails,
  getProjectTasks,
} from '@/api';

import {
  PROJECT_LIST,
  GET_PROJECT_DETAILS,
  EDIT_PROJECT,
  PROJECTS_OBJECT_ADD,
  PROJECTS_OBJECT_REMOVE,
  REORDER_PROJECT_MEDIA,
  ADD_MEDIA_URL,
  PROJECT_EDIT_DEFAULTS,
  EDIT_CATEGORIES,
  GET_PROJECT_TASKS,
  RESET_PROJECT_STATE,
  SET_ALLOWED_CHANNEL_ACCESS,
} from '@/store/mutation-types';

const defaultState = () => ({
  project_data: {
    project_name: '',
    project_description: '',
    project_virtual: '0',
    project_status: null,
  },
  project_tasks: [],
  projects_data: [],
  allowed_channel_access: [],
  projects_categories: [],
  projects_organizers: [],
});

const state = {
  project_edit_defaults: {},
  projects: [],
  project_data: {
    project_name: '',
    project_description: '',
    project_virtual: '0',
    project_status: null,
  },
  project_tasks: [],
  projects_data: [],
  allowed_channel_access: [],
  projects_categories: [],
  projects_organizers: [],
  projectEditLoading: false,
};

const getters = {
  projectEditLoading(state) {
    return state.projectEditLoading;
  },
  list(state) {
    return state.projects;
  },
  project(state) {
    return state.project_data;
  },
  categories(state) {
    return state.projects_categories || [];
  },
  medias(state) {
    return state.projects_data;
  },
  categoryOptions(state) {
    return state.project_edit_defaults.categories || [];
  },
  projectTasks(state) {
    return state.project_tasks;
  },
  organizers(state) {
    return state.projects_organizers || [];
  },
  allowedChannelAccess(state) {
    return state.allowed_channel_access || [];
  },
};


const editProjectDebounced = debounce((vm, userToken, newTask) => {
  editProject(userToken, newTask)
    .then(({ data }) => {
      const status = get(data, '[1].status');
      if (status === 'SUCCESS') {
        vm.dispatch('auth/setFlashMessage', { message: 'Project saved successfully', type: 'success' });
      } else {
        vm.dispatch('auth/setFlashMessage', { message: 'Failed to save project', type: 'error' });
      }
    })
    .catch(() => {
      vm.dispatch('auth/setFlashMessage', { message: 'Failed to save project', type: 'error' });
    });
}, 1000);

const editOrganizerDebounced = debounce((vm, userToken, newTask) => {
  editProject(userToken, newTask)
    .then(({ data }) => {
      const status = get(data, '[1].status');
      if (status === 'SUCCESS') {
        vm.dispatch('auth/setFlashMessage', { message: 'Project saved successfully', type: 'success' });
      } else {
        vm.dispatch('auth/setFlashMessage', { message: 'Failed to save project', type: 'error' });
      }
    })
    .catch(() => {
      vm.dispatch('auth/setFlashMessage', { message: 'Failed to save project', type: 'error' });
    });
}, 1000);

const editCategoriesDebounced = debounce((vm, userToken, newTask) => {
  editProject(userToken, newTask)
    .then(({ data }) => {
      const status = get(data, '[1].status');
      if (status === 'SUCCESS') {
        vm.dispatch('auth/setFlashMessage', { message: 'Project saved successfully', type: 'success' });
      } else {
        vm.dispatch('auth/setFlashMessage', { message: 'Failed to save project', type: 'error' });
      }
    })
    .catch(() => {
      vm.dispatch('auth/setFlashMessage', { message: 'Failed to save project', type: 'error' });
    });
}, 1000);

const actions = {
  resetProjectsState({ commit }) {
    commit(RESET_PROJECT_STATE);
  },
  getProjectEditDefaults({ commit }) {
    const userToken = this.getters['auth/userToken'];
    getProjectEditDefaults(userToken)
      .then(({ data }) => {
        const defaults = get(data, '[1].data.details');
        commit(PROJECT_EDIT_DEFAULTS, defaults);
      });
  },
  getProjectList({ commit }) {
    const userToken = this.getters['auth/userToken'];
    getOrganizerProjectList(userToken)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'ERROR') {
          commit(PROJECT_LIST, []);
          return;
        }
        const projects = get(data, '[1].data.details') || [];
        commit(PROJECT_LIST, projects);
      })
      .catch(() => {
        // TODO: handle the error
        // commit error as well to the state
      });
  },
  getProjectsTasks({ commit }, { projectId }) {
    const userToken = this.getters['auth/userToken'];
    getProjectTasks(userToken, projectId)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'ERROR') {
          commit(GET_PROJECT_TASKS, []);
          return;
        }
        const tasks = get(data, '[1].data.details');
        commit(GET_PROJECT_TASKS, tasks);
      });
  },
  getProjectEditDetails({ commit }, { projectId }) {
    const userToken = this.getters['auth/userToken'];
    commit('LOADING_PROJECT_EDIT', true);
    return getProjectEditDetails(userToken, projectId)
      .then(({ data }) => {
        commit('LOADING_PROJECT_EDIT', false);
        const status = get(data, '[1].status');
        if (status === 'ERROR') {
          commit(GET_PROJECT_DETAILS, {});
          return;
        }
        const project = get(data, '[1].data.details');
        commit(GET_PROJECT_DETAILS, project);
      });
  },
  getProjectDetails({ commit }, { projectId }) {
    const userToken = this.getters['auth/userToken'];
    return getProjectDetails(userToken, projectId)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'ERROR') {
          commit(GET_PROJECT_DETAILS, {});
          return;
        }
        const projectDetails = get(data, '[1].data');
        const project = get(projectDetails, 'details[0]');
        commit(GET_PROJECT_DETAILS, project);
        const allowedChannelAccess = get(projectDetails, 'details[0].project_data.channel_types_access');
        commit(SET_ALLOWED_CHANNEL_ACCESS, allowedChannelAccess);
      });
  },
  editProject({ commit, state }, project) {
    const newProject = { ...state.project_data, ...project };
    const userToken = this.getters['auth/userToken'];
    commit(EDIT_PROJECT, newProject);
    // udaate the backend version with the same data
    const vm = this;

    const { project_name: name, project_description: description } = newProject;

    if (!name || !description || name.length < 20 || description.length < 50) {
      return;
    }

    editProjectDebounced(vm, userToken, newProject);
  },
  editCategories({ commit }, values) {
    commit(EDIT_CATEGORIES, values);
    const userToken = this.getters['auth/userToken'];
    const vm = this;
    editCategoriesDebounced(vm, userToken, values);
  },
  editOrganizers(_, projectsOrganizers) {
    const userToken = this.getters['auth/userToken'];
    const vm = this;
    editOrganizerDebounced(vm, userToken, projectsOrganizers);
  },
  projectsObjectAdd({ commit }, projectObject) {
    commit(PROJECTS_OBJECT_ADD, projectObject);
  },
  addMediaUrl({ commit }, project) {
    commit(ADD_MEDIA_URL, project.projects_data);
    const dataUrl = get(project.projects_data, '[0].data_url');
    const userToken = this.getters['auth/userToken'];
    addProjectMediaUrl(userToken, dataUrl, project.project_id)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          this.dispatch('auth/setFlashMessage', { message: 'Project saved successfully', type: 'success' });
        } else {
          this.dispatch('auth/setFlashMessage', { message: 'Failed to save project', type: 'error' });
        }
      })
      .catch(() => {
        this.dispatch('auth/setFlashMessage', { message: 'Failed to save project', type: 'error' });
      });
  },
  projectsMediaObjectRemove({ commit }, { index, projectId }) {
    const userToken = this.getters['auth/userToken'];
    const projectsObject = this.getters['projects/medias'][index] || {};
    const objectId = projectsObject.data_object_id;
    removeMedia(userToken, { data_object_id: objectId, project_id: projectId })
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          this.dispatch('auth/setFlashMessage', { message: 'Project saved successfully', type: 'success' });
        } else {
          this.dispatch('auth/setFlashMessage', { message: 'Failed to save project', type: 'error' });
        }
      })
      .catch(() => {
        this.dispatch('auth/setFlashMessage', { message: 'Failed to save project', type: 'error' });
      });
    commit(PROJECTS_OBJECT_REMOVE, index);
  },
  reorderProjectMedia({ commit }, { medias, projectId }) {
    const projectDataList = [];
    const len = medias.length;
    for (let i = 0; i < medias.length; i += 1) {
      medias[i].project_data_rank = i + 1;
      const key = i + 1;
      const value = medias[i].data_object_id;
      projectDataList[i] = { [key]: value };
    }
    commit(REORDER_PROJECT_MEDIA, medias);
    const projectDataRankRange = { min_user_project_data_rank: 1,
      max_user_project_data_rank: len };
    const userToken = this.getters['auth/userToken'];
    reorderProjectMedia(userToken, projectId, projectDataRankRange, projectDataList)
      .then(({ data }) => {
        const status = get(data, '[1].status');
        if (status === 'SUCCESS') {
          this.dispatch('auth/setFlashMessage', { message: 'Project saved successfully', type: 'success' });
        } else {
          this.dispatch('auth/setFlashMessage', { message: 'Failed to save project', type: 'error' });
        }
      })
      .catch(() => {
        this.dispatch('auth/setFlashMessage', { message: 'Failed to save project', type: 'error' });
      });
  },
};

const mutations = {
  [PROJECT_LIST](state, projects) {
    state.projects = projects;
  },
  [SET_ALLOWED_CHANNEL_ACCESS](state, allowedChannelAccess) {
    state.allowed_channel_access = allowedChannelAccess;
  },
  [GET_PROJECT_DETAILS](state, project) {
    state.project_data = {
      ...state.project_data,
      ...project.project_data,
    };
    state.projects_categories = project.projects_categories;
    state.projects_organizers = project.projects_organizers;

    if (project.projects_data) {
      project.projects_data.sort((a, b) => a.project_data_rank - b.project_data_rank);
      state.projects_data = project.projects_data;
    }
  },
  [EDIT_PROJECT](state, newProject) {
    state.project_data = {
      ...state.project_data,
      ...newProject,
    };
  },
  [ADD_MEDIA_URL](state, projectsData) {
    state.projects_data = [...state.projects_data, ...projectsData];
  },
  [PROJECTS_OBJECT_ADD](state, projectObject) {
    state.projects_data = [
      ...state.projects_data,
      projectObject,
    ];
  },
  [RESET_PROJECT_STATE](state) {
    const s = defaultState();
    Object.keys(s).forEach(key => state[key] = s[key]);
  },
  [PROJECTS_OBJECT_REMOVE](state, index) {
    state.projects_data = [
      ...state.projects_data.slice(0, index),
      ...state.projects_data.slice(index + 1, state.projects_data.length),
    ];
  },
  [REORDER_PROJECT_MEDIA](state, projectMediaObject) {
    state.projects_data = projectMediaObject;
  },
  [PROJECT_EDIT_DEFAULTS](state, defaults) {
    state.project_edit_defaults = defaults;
  },
  [EDIT_CATEGORIES](state, values) {
    state.projects_categories = values.projects_categories;
  },
  [GET_PROJECT_TASKS](state, tasks) {
    state.project_tasks = tasks;
  },
  LOADING_PROJECT_EDIT(state, value) {
    state.projectEditLoading = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/projects.js